import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminServices from '../components/AdminServices';
import AdminVendors from '../components/AdminVendors';
import AdminSettings from '../components/AdminSettings';
import AdminAreas from '../components/AdminAreas';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS } from '../config/api.config';
import '../styles/admin.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');

  useEffect(() => {
    const adminAuth = localStorage.getItem('adminAuth');
    if (adminAuth) {
      try {
        const auth = JSON.parse(adminAuth);
        setAdminUsername(auth.username);
      } catch (err) {
        console.error('Error parsing admin auth:', err);
      }
    }
  }, []);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'services', label: 'Services', icon: '🔧' },
    { id: 'vendors', label: 'Vendors', icon: '👥' },
    { id: 'areas', label: 'Areas', icon: '📍' },
    { id: 'categories', label: 'Categories', icon: '📁' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get(`${ENDPOINTS.ADMIN_API}/stats`);
      setStats(response?.data || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (page) => {
    setCurrentPage(page);
    if (page === 'dashboard') {
      fetchStats();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('authToken');
    navigate('/admin-login');
  };

  return (
    <div className="admin-wrapper">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-logo">
          <h2>🏢 Admin Panel</h2>
        </div>
        
        <nav className="admin-nav">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`admin-nav-item ${currentPage === item.id ? 'active' : ''}`}
              onClick={() => handleNavigation(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <button className="btn btn-logout" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-main">
        <header className="admin-header">
          <h1>Admin Control Panel</h1>
          <div className="admin-user-info">
            <span>👤 {adminUsername || 'Admin User'}</span>
          </div>
        </header>

        <div className="admin-content">
          {currentPage === 'dashboard' && <AdminDashboardContent stats={stats} loading={loading} />}
          {currentPage === 'services' && <AdminServices />}
          {currentPage === 'vendors' && <AdminVendors />}
          {currentPage === 'areas' && <AdminAreas />}
          {currentPage === 'categories' && <AdminCategories />}
          {currentPage === 'settings' && <AdminSettings />}
        </div>
      </div>
    </div>
  );
};

// Dashboard Content
const AdminDashboardContent = ({ stats, loading }) => {
    console.log('Admin stats:', stats);
  return (
    <div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '1.5rem' }}>⏳ Loading analytics...</div>
        </div>
      ) : stats ? (
        <>
          <div className="dashboard-grid">
            <div className="stat-card">
              <div className="stat-icon">🔧</div>
              <div className="stat-content">
                <h3>Total Services</h3>
                <p className="stat-number">{stats.total_services || 0}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>Total Vendors</h3>
                <p className="stat-number">{stats.total_vendors || 0}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-content">
                <h3>Avg Rating</h3>
                <p className="stat-number">{stats.avg_rating || 0}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>Verified Vendors</h3>
                <p className="stat-number">{stats.verified_vendors || 0}</p>
              </div>
            </div>
          </div>

          <div className="dashboard-grid" style={{ marginTop: '20px' }}>
            <div className="alert-card alert-warning">
              <div className="alert-icon">⚠️</div>
              <div className="alert-content">
                <h3>Incomplete Services</h3>
                <p className="alert-text">{stats.incomplete_services || 0} services missing required fields</p>
              </div>
            </div>

            <div className="alert-card alert-warning">
              <div className="alert-icon">⚠️</div>
              <div className="alert-content">
                <h3>Incomplete Vendors</h3>
                <p className="alert-text">{stats.incomplete_vendors || 0} vendors missing required fields</p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '1.2rem', color: 'var(--admin-text-secondary)' }}>No data available</div>
        </div>
      )}
    </div>
  );
};

// Categories Management Component
const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get(ENDPOINTS.CATEGORIES);
      setCategories(response || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>📁 Categories Management</h2>
        <button className="btn btn-primary">➕ Add Category</button>
      </div>

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Category Name</th>
              <th>Parent Category</th>
              <th>Services Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '12px' }}>
                  Loading...
                </td>
              </tr>
            ) : categories.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '12px' }}>
                  No categories found.
                </td>
              </tr>
            ) : (
              categories.map(category => (
                <tr key={category.id}>
                  <td>
                    <strong>{category.name}</strong>
                  </td>
                  <td>{category.parent_id ? `ID: ${category.parent_id}` : '-'}</td>
                  <td>0</td>
                  <td>
                    <div className="actions" style={{ display: 'flex', gap: '6px' }}>
                      <button className="btn btn-edit">Edit</button>
                      <button className="btn btn-danger">Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard;
